import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Autocomplete,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const money = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};
const isoDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

export default function CustomerStatement() {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/accounts")
      .then((r) => setAccounts((r.data || []).filter((a) => a.BusinessType !== "Employee")))
      .catch(() => setAccounts([]));
  }, []);

  const run = useCallback(() => {
    if (!account) { setError("Pick a customer or vendor"); return; }
    if (!from || !to) { setError("Pick a date range"); return; }
    setLoading(true); setError(null); setData(null);
    api.get("/generalLedger", { params: { fromDate: from, toDate: to, accountName: account.SubAccount_Name } })
      .then((r) => {
        const exact = (r.data.accounts || []).find(
          (a) => a.account.trim().toUpperCase() === account.SubAccount_Name.trim().toUpperCase()
        );
        setData(exact || { account: account.SubAccount_Name, opening: 0, transactions: [], closing: 0 });
      })
      .catch((e) => setError(e.response?.data?.error || "Failed to build statement"))
      .finally(() => setLoading(false));
  }, [account, from, to]);

  // Running balance from opening.
  let running = data ? Number(data.opening) || 0 : 0;
  const rows = data ? [...(data.transactions || [])]
    .sort((a, b) => isoDate(a.date).localeCompare(isoDate(b.date)))
    .map((t) => { running += Number(t.amount) || 0; return { ...t, balance: running }; }) : [];
  const closing = data ? Number(data.closing) || 0 : 0;
  const limit = account && account.CreditLimit != null ? Number(account.CreditLimit) : null;
  const overLimit = limit != null && closing > limit + 0.005;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Customer / Vendor Statement</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        A printable statement of account — opening balance, every transaction, and the closing balance due.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }} className="no-print">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Autocomplete
            options={accounts} value={account} onChange={(_e, v) => setAccount(v)}
            getOptionLabel={(o) => o.SubAccount_Name || ""}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 280 }} size="small"
            renderInput={(params) => <TextField {...params} label="Customer / Vendor" />}
          />
          <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run"}
          </Button>
          {data && <Button variant="outlined" onClick={() => window.print()}>Print</Button>}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Statement of Account</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{data.account}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{from} to {to}</Typography>
            {account && account.PaymentTermsDays != null && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Terms: Net {account.PaymentTermsDays} days</Typography>
            )}
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontStyle: "italic", color: "text.secondary" }}>Opening balance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{money(data.opening)}</TableCell>
                </TableRow>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={5} sx={{ color: "text.secondary" }}>No transactions in this range.</TableCell></TableRow>
                )}
                {rows.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ py: 0.4, whiteSpace: "nowrap" }}>{isoDate(t.date)}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>{t.type}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>{t.ref}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{money(t.amount)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{money(t.balance)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800 }}>Balance due</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>{money(closing)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {overLimit && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Balance due exceeds this customer's credit limit of {money(limit)}.
            </Alert>
          )}

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Positive = owed to you (charges/invoices); negative in the Amount column = payments received. Balance is what
            they owe as of each line. Same figures as the General Ledger.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
